import React, { useState, useEffect } from 'react';
import {
  useGetQuizAttemptsQuery,
  useGetProductsQuery,
} from './productKnowledgeApiSlice';
import { useGetUsersQuery } from '../users/usersApiSlice';
import {
  Header,
  Container,
  Table,
  Segment,
  Loader,
  Icon,
  Input,
  Button,
  Label,
  Pagination,
  Modal,
} from 'semantic-ui-react';
import CustomDropdown from '../../components/CustomDropdown'; // Assuming this exists as in ChatSessionsList
import { useLocation, useNavigate } from 'react-router-dom';
import { PAGE_SIZE } from '../../constants';

// Two lines, then "…": the cell stays scannable and the row's detail panel has the full text.
const clamp2 = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  whiteSpace: 'pre-line',
  wordBreak: 'break-word',
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={{ fontSize: '12px', color: '#86868b', marginBottom: '4px' }}>{label}</div>
    <div style={{ fontSize: '14px', color: '#1d1d1f', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{children}</div>
  </div>
);

const QuizAttemptManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get('page');
  const page_size = queryParams.get('page_size') || PAGE_SIZE;
  const start_date = queryParams.get('start_date');
  const end_date = queryParams.get('end_date');
  const search = queryParams.get('search');
  const product = queryParams.get('product');
  const ordering = queryParams.get('ordering');
  const trainee = queryParams.get('trainee');
  const status = queryParams.get('status');

  const [searchValue, setSearchValue] = useState(search || '');
  const [selected, setSelected] = useState(null);

  // Filter params
  let qp = {
    page,
    page_size,
    start_date,
    end_date,
    search,
    product,
    ordering,
    trainee,
    status
  };
  qp = Object.entries(qp)
    .filter(e => e[1])
    .reduce((accumulator, [k, v]) => {
      accumulator[k] = v;
      return accumulator;
    }, {});

  const { data: attempts, isLoading } = useGetQuizAttemptsQuery({...qp});
  // Fetch a larger set of products to ensure client-side search works across all items
  const { data: productsData } = useGetProductsQuery({ page_size: 1000 });
  const { data: usersData } = useGetUsersQuery({ page_size: 1000 });

  const products = Array.isArray(productsData) ? productsData : productsData?.results || [];
  const productOptions = products.map(p => ({
    key: p.id,
    text: p.name,
    value: p.id,
  }));

  const users = Array.isArray(usersData) ? usersData : usersData?.results || [];
  const userOptions = users.map(u => ({
    key: u.id,
    text: `${u.full_name || u.username} (${u.username})`,
    value: u.id,
  }));

  const statusOptions = [
    { key: 'all', text: '전체 상태', value: '' },
    { key: 'correct', text: '정답', value: 'correct' },
    { key: 'incorrect', text: '오답', value: 'incorrect' },
  ];

  const handleFilterChange = (key, value) => {
    const searchParams = new URLSearchParams(location.search);
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleSearch = () => {
    const searchParams = new URLSearchParams(location.search);
    if (searchValue) {
      searchParams.set('search', searchValue);
    } else {
      searchParams.delete('search');
    }
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleSort = clickedColumn => {
    const searchParams = new URLSearchParams(location.search);
    const currentOrdering = searchParams.get('ordering');

    let newOrdering = clickedColumn;
    if (currentOrdering === clickedColumn) {
      newOrdering = `-${clickedColumn}`;
    } else if (currentOrdering === `-${clickedColumn}`) {
      newOrdering = clickedColumn;
    }

    searchParams.set('ordering', newOrdering);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const getSortIcon = column => {
    if (ordering === column) return 'sort up';
    if (ordering === `-${column}`) return 'sort down';
    return 'sort';
  };

  const handlePaginationChange = (e, { activePage }) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', activePage);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  if (isLoading) return <Loader active />;

  return (
    <div
      style={{
        height: 'auto',
        minHeight: '100vh',
        display: isMobile ? 'block' : 'flex',
        flexDirection: 'column',
        overflow: 'visible',
      }}
    >
      {/* Header Section */}
      <div
        style={{
          flex: '0 0 auto',
          padding: '16px 0',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
          marginBottom: isMobile ? '20px' : '0',
          zIndex: 100,
        }}
      >
        <Container fluid>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '0 20px',
            }}
          >
            <div
              style={{
                width: '40px',
                flexShrink: 0, // stays a circle when the text beside it wraps
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#F2F2F7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon
                name="history"
                style={{ margin: 0, color: '#007AFF', fontSize: '18px' }}
              />
            </div>
            <div>
              <h2
                style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  margin: 0,
                  color: '#1d1d1f',
                }}
              >
                퀴즈 내역
              </h2>
              <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>
                View user quiz attempts
              </p>
            </div>
          </div>
        </Container>
      </div>

       <Container
        fluid
        style={{
          padding: isMobile ? '0 20px' : '20px',
          flex: 1,
          display: isMobile ? 'block' : 'flex',
          flexDirection: 'column',
          overflow: 'visible',
        }}
      >
        
        {/* Filter Bar */}
        <div
          style={{
            marginBottom: '24px',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '12px',
            flexWrap: 'wrap',
            alignItems: isMobile ? 'stretch' : 'center',
            position: 'relative',
            zIndex: 10,
          }}
        >
          {/* Date Range Picker */}
          <div
            style={{
              flex: '0 1 auto',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#F5F5F7',
              borderRadius: '12px',
              padding: '0 16px',
              gap: '8px',
              height: '48px',
            }}
          >
            <Input
              type="date"
              value={queryParams.get('start_date') || ''}
              onChange={e => handleFilterChange('start_date', e.target.value)}
              transparent
              style={{
                fontSize: '14px',
                width: '130px',
                fontFamily: 'inherit',
                color: '#1d1d1f',
              }}
            />
            <span
              style={{
                color: '#86868b',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              -
            </span>
            <Input
              type="date"
              value={queryParams.get('end_date') || ''}
              onChange={e => handleFilterChange('end_date', e.target.value)}
              transparent
              style={{
                fontSize: '14px',
                width: '130px',
                fontFamily: 'inherit',
                color: '#1d1d1f',
              }}
            />
            {(queryParams.get('start_date') || queryParams.get('end_date')) && (
                <Icon
                  name="close"
                  size={'small'}
                  style={{
                    borderWidth: 0,
                    border: 0,
                    color: '#86868b',
                    margin: 0,
                    marginLeft: '4px',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    const searchParams = new URLSearchParams(location.search);
                    searchParams.delete('start_date');
                    searchParams.delete('end_date');
                    searchParams.set('page', 1);
                    navigate(`${location.pathname}?${searchParams.toString()}`);
                  }}
                />
              )}
          </div>

          {/* Product Dropdown */}
          <CustomDropdown
            placeholder="제품 선택"
            options={productOptions}
            value={product || ''}
            onChange={value => handleFilterChange('product', value)}
            icon="box"
            style={{ minWidth: '150px' }}
            searchable
          />

          {/* User Dropdown */}
          <CustomDropdown
            placeholder="직원 선택"
            options={userOptions}
            value={trainee || ''}
            onChange={value => handleFilterChange('trainee', value)}
            icon="user"
            style={{ minWidth: '150px' }}
            searchable
          />

          {/* Status Dropdown */}
          <CustomDropdown
            placeholder="상태 선택"
            options={statusOptions}
            value={status || ''}
            onChange={value => handleFilterChange('status', value)}
            icon="filter"
            style={{ minWidth: '120px' }}
          />

          {/* Search Input */}
          <div
            style={{
              flex: '1 1 auto',
              display: 'flex',
              alignItems: 'center',
              alignContent: 'center',
              justifyContent: 'flex-start',
              backgroundColor: '#F5F5F7',
              borderRadius: '12px',
              padding: '0 16px',
              height: '48px',
              gap: '12px',
              transition: 'all 0.2s ease',
            }}
          >
            <Icon
              name="search"
              style={{ color: '#86868b', margin: 0, fontSize: '16px' }}
            />
            <Input
              placeholder="검색 (내용, 사용자, 제품)..."
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              transparent
              fluid
              style={{
                fontSize: '13px',
                width: '100%',
                fontFamily: 'inherit',
                color: '#1d1d1f',
              }}
            />
            {searchValue && (
              <Icon
                name="close"
                link
                style={{ color: '#86868b', margin: 0, fontSize: '13px' }}
                onClick={e => {
                  e.stopPropagation();
                  setSearchValue('');
                  const searchParams = new URLSearchParams(location.search);
                  searchParams.delete('search');
                  navigate(`${location.pathname}?${searchParams.toString()}`);
                }}
              />
            )}
          </div>
          
           {/* Search Button */}
           <div
            style={{
              marginLeft: isMobile ? '0' : 'auto',
              width: isMobile ? '100%' : 'auto',
              display: 'flex',
              gap: '8px',
            }}
          >
            <Button
              primary
              icon
              labelPosition="right"
              onClick={handleSearch}
              fluid={isMobile}
              style={{
                borderRadius: '30px',
                height: '48px',
                backgroundColor: '#0091FF',
                paddingLeft: '16px',
                paddingRight: '16px',
                // display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              검색
              <Icon
                name="arrow right"
                style={{ marginLeft: '8px', margin: 0 }}
              />
            </Button>
          </div>
        </div>

      <div
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '20px',
            border: '1px solid rgba(0,0,0,0.04)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.02)',
            overflow: 'visible',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
        <div style={{ overflow: 'visible', flex: 1 }}>
        <Table
          compact="very"
          selectable
          style={{ margin: 0, border: 'none' }}
        >
          <Table.Header
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: 'white',
            }}
          >
            <Table.Row>
              <Table.HeaderCell 
                style={{ 
                    borderBottom: '1px solid rgba(34,36,38,.15)',
                    paddingLeft: '20px',
                    cursor: 'pointer',
                }}
                onClick={() => handleSort('created_at')}
              >
                일시 <Icon name={getSortIcon('created_at')} />
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{
                  borderBottom: '1px solid rgba(34,36,38,.15)',
                  cursor: 'pointer',
                }}
                onClick={() => handleSort('question__product__name')}
              >
                제품 <Icon name={getSortIcon('question__product__name')} />
              </Table.HeaderCell>
              <Table.HeaderCell 
                style={{ 
                    borderBottom: '1px solid rgba(34,36,38,.15)',
                    cursor: 'pointer',
                }}
                onClick={() => handleSort('user__username')}
              >
                직원 <Icon name={getSortIcon('user__username')} />
              </Table.HeaderCell>
              <Table.HeaderCell 
                style={{ 
                    borderBottom: '1px solid rgba(34,36,38,.15)',
                    cursor: 'pointer',
                }}
                onClick={() => handleSort('question__question_text')}
              >
                문제 <Icon name={getSortIcon('question__question_text')} />
              </Table.HeaderCell>
              <Table.HeaderCell 
                style={{ 
                    borderBottom: '1px solid rgba(34,36,38,.15)',
                    cursor: 'pointer',
                }}
                onClick={() => handleSort('question__question_type')}
              >
                유형 <Icon name={getSortIcon('question__question_type')} />
              </Table.HeaderCell>
              <Table.HeaderCell 
                style={{ 
                    borderBottom: '1px solid rgba(34,36,38,.15)',
                    cursor: 'pointer',
                }}
                onClick={() => handleSort('is_correct')}
              >
                정답 여부 <Icon name={getSortIcon('is_correct')} />
              </Table.HeaderCell>
              <Table.HeaderCell style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}>제출 답안</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {attempts?.results?.map(attempt => (
              <Table.Row key={attempt.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(attempt)}>
                <Table.Cell style={{ whiteSpace: 'nowrap', color: '#86868b', paddingLeft: '20px' }}>{new Date(attempt.created_at).toLocaleString()}</Table.Cell>
                <Table.Cell style={{ fontWeight: 'bold' }}>{attempt.product_name || '-'}</Table.Cell>
                <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                  {/* username is a Firebase UID for social sign-ins — show the person */}
                  <div>{attempt.user_display_name || attempt.user_username}</div>
                  {attempt.user_contractor && (
                    <div style={{ fontSize: '12px', color: '#86868b' }}>{attempt.user_contractor}</div>
                  )}
                </Table.Cell>
                <Table.Cell style={{ maxWidth: '360px' }}>
                  <div style={clamp2}>{attempt.question_text || attempt.question}</div>
                </Table.Cell>
                <Table.Cell>
                    <Label size='mini' color='grey' basic>
                    {attempt.question_type}
                    </Label>
                </Table.Cell>
                <Table.Cell>
                  {attempt.is_correct ? <Icon name='checkmark' color='green' /> : <Icon name='close' color='red' />}
                </Table.Cell>
                <Table.Cell style={{ maxWidth: '260px' }}>
                  <div style={clamp2}>{attempt.user_answer}</div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        </div>
        {/* Pagination */}
        <div
          style={{
            padding: '12px',
            borderTop: '1px solid rgba(0,0,0,0.05)',
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: '#f8f9fa',
          }}
        >
          <Pagination
            activePage={page || 1}
            onPageChange={handlePaginationChange}
            totalPages={Math.ceil((attempts?.count || 0) / (page_size || PAGE_SIZE))}
            size="mini"
            ellipsisItem={{
              content: <Icon name="ellipsis horizontal" />,
              icon: true,
            }}
            firstItem={{
              content: <Icon name="angle double left" />,
              icon: true,
            }}
            lastItem={{
              content: <Icon name="angle double right" />,
              icon: true,
            }}
            prevItem={{ content: <Icon name="angle left" />, icon: true }}
            nextItem={{
              content: <Icon name="angle right" />,
              icon: true,
            }}
          />
        </div>
      </div>
    </Container>

      {/* Row detail: the full question, answer and grading feedback the table clamps. */}
      <Modal open={!!selected} onClose={() => setSelected(null)} size="small" closeIcon>
        {selected && (
          <>
            <Modal.Header style={{ fontSize: '16px' }}>
              {selected.user_display_name || selected.user_username}
              {selected.user_contractor ? ` · ${selected.user_contractor}` : ''}
              <span style={{ fontSize: '13px', fontWeight: 400, color: '#86868b', marginLeft: '8px' }}>
                {new Date(selected.created_at).toLocaleString()}
              </span>
            </Modal.Header>
            <Modal.Content scrolling>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <Label basic>{selected.product_name || '-'}</Label>
                <Label basic>{selected.question_type}</Label>
                <Label color={selected.is_correct ? 'green' : 'red'} basic>
                  {selected.is_correct ? '정답' : '오답'}
                </Label>
              </div>
              <Field label="문제">{selected.question_text}</Field>
              <Field label="제출 답안">{selected.user_answer || '-'}</Field>
              {selected.grading_feedback && <Field label="채점 피드백">{selected.grading_feedback}</Field>}
              <Field label="다음 복습">
                {new Date(selected.next_review_at).toLocaleDateString()} (간격 {selected.review_interval}일)
              </Field>
            </Modal.Content>
            <Modal.Actions>
              <Button
                basic
                onClick={() => {
                  setSelected(null);
                  handleFilterChange('trainee', selected.user);
                }}
              >
                이 직원의 내역만 보기
              </Button>
              <Button primary onClick={() => navigate(`/admin/quiz-questions/${selected.question}`)}>
                문제 상세
              </Button>
            </Modal.Actions>
          </>
        )}
      </Modal>
    </div>
  );
};

export default QuizAttemptManagement;
